// Live OpenRouter model catalog, filtered to the 7 frontier labs we
// surface in the BYOK/Admin model pickers. Fetched from the public
// `/models` endpoint (no auth required) and cached via React Query.

import { useQuery } from "@tanstack/react-query";

export type OpenRouterLab =
  | "Anthropic"
  | "OpenAI"
  | "Google"
  | "xAI"
  | "Meta"
  | "DeepSeek"
  | "Qwen";

export type OpenRouterModel = {
  id: string;              // e.g. "anthropic/claude-3.5-sonnet"
  name: string;
  lab: OpenRouterLab;
  contextLength?: number;
  created?: number;        // epoch seconds
};

const LAB_BY_PREFIX: Record<string, OpenRouterLab> = {
  "anthropic": "Anthropic",
  "openai": "OpenAI",
  "google": "Google",
  "x-ai": "xAI",
  "meta-llama": "Meta",
  "deepseek": "DeepSeek",
  "qwen": "Qwen",
};

export const OPENROUTER_LAB_ORDER: OpenRouterLab[] = [
  "Anthropic", "OpenAI", "Google", "xAI", "Meta", "DeepSeek", "Qwen",
];

// Safety net: shown if the live fetch fails so the picker is never empty.
// Two per lab: current flagship + fast/cheap. User can always type a
// custom slug regardless.
export const OPENROUTER_SAFETY_NET: OpenRouterModel[] = [
  { id: "anthropic/claude-sonnet-4.5",       name: "Claude Sonnet 4.5",     lab: "Anthropic" },
  { id: "anthropic/claude-3.5-haiku",        name: "Claude 3.5 Haiku",      lab: "Anthropic" },
  { id: "openai/gpt-5.5",                    name: "GPT-5.5",               lab: "OpenAI" },
  { id: "openai/gpt-5-mini",                 name: "GPT-5 Mini",            lab: "OpenAI" },
  { id: "google/gemini-2.5-pro",             name: "Gemini 2.5 Pro",        lab: "Google" },
  { id: "google/gemini-2.5-flash",           name: "Gemini 2.5 Flash",      lab: "Google" },
  { id: "x-ai/grok-4",                       name: "Grok 4",                lab: "xAI" },
  { id: "x-ai/grok-3",                       name: "Grok 3",                lab: "xAI" },
  { id: "meta-llama/llama-4-maverick",       name: "Llama 4 Maverick",      lab: "Meta" },
  { id: "meta-llama/llama-3.3-70b-instruct", name: "Llama 3.3 70B Instruct", lab: "Meta" },
  { id: "deepseek/deepseek-v3.1",            name: "DeepSeek V3.1",         lab: "DeepSeek" },
  { id: "deepseek/deepseek-r1",              name: "DeepSeek R1",           lab: "DeepSeek" },
  { id: "qwen/qwen3-235b-a22b",              name: "Qwen3 235B A22B",       lab: "Qwen" },
  { id: "qwen/qwen3-coder",                  name: "Qwen3 Coder",           lab: "Qwen" },
];

type RawModel = {
  id: string;
  name?: string;
  context_length?: number;
  created?: number;
};

const parseCatalog = (raw: RawModel[]): OpenRouterModel[] => {
  const out: OpenRouterModel[] = [];
  for (const m of raw) {
    if (!m?.id || typeof m.id !== "string") continue;
    // Exclude "~vendor/*" mirror/variant prefixes.
    if (m.id.startsWith("~")) continue;
    const prefix = m.id.split("/")[0];
    const lab = LAB_BY_PREFIX[prefix];
    if (!lab) continue;
    out.push({
      id: m.id,
      name: m.name || m.id,
      lab,
      contextLength: m.context_length,
      created: m.created,
    });
  }
  // Sort per-lab by created desc so the newest models appear first
  // within each provider section.
  out.sort((a, b) => (b.created ?? 0) - (a.created ?? 0));
  return out;
};

/**
 * Fetches the OpenRouter model catalog once per session, filtered to
 * the 7 frontier labs. Falls back to the static safety net on error.
 * `enabled` is used to defer the network call until the first popover
 * open.
 */
export const useOpenRouterModelCatalog = (enabled: boolean) => {
  return useQuery({
    queryKey: ["openrouter-model-catalog"],
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    queryFn: async (): Promise<OpenRouterModel[]> => {
      const res = await fetch("https://openrouter.ai/api/v1/models");
      if (!res.ok) throw new Error(`OpenRouter models fetch failed: ${res.status}`);
      const body = await res.json();
      const rows = Array.isArray(body?.data) ? (body.data as RawModel[]) : [];
      const parsed = parseCatalog(rows);
      return parsed.length > 0 ? parsed : OPENROUTER_SAFETY_NET;
    },
  });
};

export const formatContextLength = (n?: number): string | null => {
  if (!n || n <= 0) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return String(n);
};
